import * as THREE from 'three';
import type { Door, Equipment, Floor, Zone } from '@/content/schema';
import { box, litMat, makeFloorText, makeLabel, mat, uniqueMat } from './builders';
import { isNewStyle } from '@/ui/artStyle';
import { PALETTE, floorColorNew } from './palette';
import { beltTexture, boardTexture, panelTexture, screenTexture } from './details';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { rect, type Rect } from './collision';
import { tr } from '@/i18n';

export const LEVEL_TINT: Record<number, string> = { [-1]: '#b9b1a0', 0: '#8fa4ae', 1: '#e3b341', 2: '#5fb38a' };
export const LEVEL_NAME: Record<number, string> = { [-1]: tr("非生產區"), 0: tr("一般作業區"), 1: tr("準清潔區"), 2: tr("清潔區") };

export interface ConveyorRt { eq: Equipment; items: THREE.Mesh[]; axis: 'x' | 'z'; len: number; spacing: number; line?: string; product?: string }
export interface EquipRuntime { data: Equipment; group: THREE.Group; box: Rect; label: THREE.Sprite; screen?: THREE.Mesh }
export interface DoorRuntime {
  data: Door; box: Rect; axis: 'x' | 'z'; blocker?: THREE.Object3D;
  /** 手動門（推／拉開）：open 狀態、關著時的碰撞框、門板 */
  open?: boolean; solid?: Rect; panels?: THREE.Object3D[];
}
export interface FloorRuntime {
  floor: Floor; group: THREE.Group; solids: Rect[];
  equipment: Map<string, EquipRuntime>; doors: Map<string, DoorRuntime>; conveyors: ConveyorRt[];
  zoneAt(x: number, z: number): Zone | null;
  anchor(id: string): { x: number; z: number; box: Rect } | null;
  walkable(x: number, z: number, r?: number, ignoreManualDoors?: boolean): boolean;
  /** 推開／關上手動門（冷凍冷藏庫、碼頭拉門） */
  setDoorOpen(id: string, open: boolean): void;
}

const WALL_T = 0.24;

/** 依樓層 JSON 生成 3D 場景、碰撞框、區域查詢。opts.flags 用來決定暗門是否已打開 */
export function buildFloor(floor: Floor, opts: { flags: Record<string, boolean> } = { flags: {} }): FloorRuntime {
  const group = new THREE.Group();
  const [W, D] = floor.size;
  const solids: Rect[] = [];

  const ground = new THREE.Mesh(new THREE.PlaneGeometry(W, D), mat(floor.theme.floor));
  ground.rotation.x = -Math.PI / 2; ground.userData.kind = 'floor'; group.add(ground);
  const M = Math.max(W, D);
  const grid = new THREE.GridHelper(M, M, floor.theme.grid[0], floor.theme.grid[1]);
  grid.position.y = 0.002; grid.scale.set(W / M, 1, D / M); group.add(grid);
  if (isNewStyle()) { const gm = grid.material as THREE.Material; gm.transparent = true; gm.opacity = 0.18; }

  // 分區：地面染色＋名稱
  floor.zones.forEach((z, zi) => {
    const [x0, z0, w, d] = z.rect;
    // v0.7 地板材質：清潔區青藍 epoxy、準清潔區綠 epoxy（調理區灰綠防滑磁磚）、冰箱與一般作業區灰 epoxy、非生產區灰白平地
    if (!z.dark) {
      const surf = new THREE.Mesh(new THREE.PlaneGeometry(w, d), floorMaterial(floorKind(z), w, d));
      surf.rotation.x = -Math.PI / 2; surf.position.set(x0 + w / 2, 0.004 + zi * 0.0003, z0 + d / 2); surf.userData.kind = 'floor'; group.add(surf);
    }
    if (floor.zones.length > 1 && w >= 3 && !z.hidden) {
      const t = makeFloorText(z.name.split(tr("（"))[0].trim(), z.level > 0 ? LEVEL_TINT[z.level] : '#e8eef0', Math.min(w - 0.4, 5));
      t.position.set(x0 + w / 2, 0.02, z0 + d - (d > 6 ? 1.6 : 0.6)); group.add(t);
    }
  });

  // 外牆（後、左）
  const WALL_C = isNewStyle() ? PALETTE.wall : floor.theme.wall;   // v1.4 新版：牆一律深灰藍
  const outer = (cx: number, cz: number, w: number, d: number, h = 3) => { const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat(WALL_C)); m.position.set(cx, h / 2, cz); group.add(m); };
  if (floor.fence) {
    // 廠區圍籬：地圖邊界（四邊），矮矮的欄杆，看得出「外面就不能走了」
    const fm = mat('#6b7a62'), pm = mat('#4f5a48');
    const rail = (cx: number, cz: number, w: number, d: number) => { for (const y of [0.45, 0.9]) { const m = new THREE.Mesh(new THREE.BoxGeometry(w, 0.06, d), fm); m.position.set(cx, y, cz); group.add(m); } };
    rail(0, -D / 2 + 0.1, W, 0.06); rail(0, D / 2 - 0.1, W, 0.06); rail(-W / 2 + 0.1, 0, 0.06, D); rail(W / 2 - 0.1, 0, 0.06, D);
    for (let x = -W / 2 + 0.1; x <= W / 2; x += 2) for (const z of [-D / 2 + 0.1, D / 2 - 0.1]) { const p = new THREE.Mesh(new THREE.BoxGeometry(0.1, 1.1, 0.1), pm); p.position.set(x, 0.55, z); group.add(p); }
    for (let z = -D / 2 + 0.1; z <= D / 2; z += 2) for (const x of [-W / 2 + 0.1, W / 2 - 0.1]) { const p = new THREE.Mesh(new THREE.BoxGeometry(0.1, 1.1, 0.1), pm); p.position.set(x, 0.55, z); group.add(p); }
  } else { outer(0, -D / 2 - 0.15, W + 0.3, 0.3); outer(-W / 2 - 0.15, 0, 0.3, D); }

  // 內牆（扣掉門洞）
  const doorsRt = new Map<string, DoorRuntime>();
  for (const d of floor.doors) {
    const onX = floor.walls.some(([x1, z1, x2, z2]) => x1 === x2 && Math.abs(x1 - d.pos[0]) < 0.01 && d.pos[1] >= Math.min(z1, z2) && d.pos[1] <= Math.max(z1, z2));
    const axis: 'x' | 'z' = onX ? 'z' : 'x';
    const box = axis === 'z' ? rect(d.pos[0], d.pos[1], 1.4, d.width) : rect(d.pos[0], d.pos[1], d.width, 1.4);
    doorsRt.set(d.id, { data: d, box, axis });
  }
  const wallMat = mat(WALL_C, undefined, 0.62);
  const wallSolid = mat(WALL_C);
  for (const [x1, z1, x2, z2] of floor.walls) {
    const vertical = x1 === x2;
    const a = vertical ? Math.min(z1, z2) : Math.min(x1, x2), b = vertical ? Math.max(z1, z2) : Math.max(x1, x2);
    const cuts = floor.doors.filter(d => vertical
      ? Math.abs(d.pos[0] - x1) < 0.01 && d.pos[1] >= a && d.pos[1] <= b
      : Math.abs(d.pos[1] - z1) < 0.01 && d.pos[0] >= a && d.pos[0] <= b)
      .map(d => { const c = vertical ? d.pos[1] : d.pos[0]; return [c - d.width / 2, c + d.width / 2] as [number, number]; })
      .sort((p, q) => p[0] - q[0]);
    let cur = a; const segs: [number, number][] = [];
    for (const [s, e] of cuts) { if (s > cur) segs.push([cur, s]); cur = Math.max(cur, e); }
    if (cur < b) segs.push([cur, b]);
    for (const [s, e] of segs) {
      const len = e - s, mid = (s + e) / 2, h = vertical ? 2.4 : 1.5;
      const m = new THREE.Mesh(vertical ? new THREE.BoxGeometry(WALL_T, h, len) : new THREE.BoxGeometry(len, h, WALL_T), vertical ? wallSolid : wallMat);
      if (vertical) { m.position.set(x1, h / 2, mid); solids.push(rect(x1, mid, WALL_T, len)); }
      else { m.position.set(mid, h / 2, z1); solids.push(rect(mid, z1, len, WALL_T)); }
      group.add(m);
    }
  }
  // 門
  const DOOR_COLOR: Record<Door['type'], string> = { airshower: '#5aa9e6', oneway: '#5fb38a', exit: '#e0a35c', open: '#9aa7ad', locked: '#d9534a', hidden: WALL_C };
  const doorSolid = (d: Door, axis: 'x' | 'z') => axis === 'x' ? rect(d.pos[0], d.pos[1], d.width, 0.3) : rect(d.pos[0], d.pos[1], 0.3, d.width);
  for (const rt of doorsRt.values()) {
    const { data: d, axis } = rt;
    const found = d.type !== 'hidden' || !!opts.flags[d.flag ?? ''];
    let c = d.cold ? '#9fd3f5' : DOOR_COLOR[d.type];
    if (d.type === 'hidden' && found) c = '#c8b48a';
    const half = d.width / 2;
    if (d.type === 'hidden' && !found) {
      const panel = new THREE.Mesh(axis === 'x' ? new THREE.BoxGeometry(d.width, 2.4, WALL_T) : new THREE.BoxGeometry(WALL_T, 2.4, d.width), wallSolid);
      panel.position.set(d.pos[0], 1.2, d.pos[1]); group.add(panel); rt.blocker = panel;
      solids.push(axis === 'x' ? rect(d.pos[0], d.pos[1], d.width, WALL_T) : rect(d.pos[0], d.pos[1], WALL_T, d.width));
      continue;
    }
    for (const s of [-1, 1]) {
      const post = new THREE.Mesh(new THREE.BoxGeometry(0.16, 2.3, 0.16), mat(c));
      post.position.set(d.pos[0] + (axis === 'x' ? s * half : 0), 1.15, d.pos[1] + (axis === 'z' ? s * half : 0)); group.add(post);
    }
    const bar = new THREE.Mesh(axis === 'x' ? new THREE.BoxGeometry(d.width, 0.16, 0.16) : new THREE.BoxGeometry(0.16, 0.16, d.width), mat(c, c));
    bar.position.set(d.pos[0], 2.3, d.pos[1]); group.add(bar);
    if (d.type === 'airshower') {
      const glass = new THREE.Mesh(axis === 'x' ? new THREE.BoxGeometry(d.width, 2.2, 1.3) : new THREE.BoxGeometry(1.3, 2.2, d.width),
        litMat({ color: c, transparent: true, opacity: 0.16, depthWrite: false }));
      glass.position.set(d.pos[0], 1.1, d.pos[1]); group.add(glass);
      for (let i = 0; i < 6; i++) { // 噴嘴
        const n = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.08, 8), mat('#d9e2e6'));
        const along = -half + 0.2 + (i % 3) * (d.width - 0.4) / 2, side = i < 3 ? -0.62 : 0.62;
        n.position.set(d.pos[0] + (axis === 'x' ? along : side), 0.6 + (i % 2) * 0.8, d.pos[1] + (axis === 'z' ? along : side)); group.add(n);
      }
    }
    if (d.type === 'locked') { const lk = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.26, 0.12), mat('#d6a23c')); lk.position.set(d.pos[0], 1.2, d.pos[1]); group.add(lk); }
    if (d.manual) {
      // 手動門：冷凍冷藏庫是厚的保溫門（推開會轉開）；碼頭是兩片拉門（往兩邊拉開）
      const panelMat = litMat({ color: d.cold ? '#dcebf2' : '#b8c4c9', transparent: true, opacity: d.slide ? 0.9 : 0.95 });
      const panels: THREE.Object3D[] = [];
      const n = d.slide ? 2 : 1, pw = d.width / n;
      for (let i = 0; i < n; i++) {
        const piv = new THREE.Group();
        const off = d.slide ? (i === 0 ? -pw / 2 : pw / 2) : -half;           // 轉軸（推門在門邊、拉門在中心）
        piv.position.set(d.pos[0] + (axis === 'x' ? off : 0), 0, d.pos[1] + (axis === 'z' ? off : 0));
        const slab = new THREE.Mesh(axis === 'x' ? new THREE.BoxGeometry(pw - 0.04, 2.1, 0.12) : new THREE.BoxGeometry(0.12, 2.1, pw - 0.04), panelMat);
        slab.position.set(d.slide ? 0 : (axis === 'x' ? pw / 2 : 0), 1.05, d.slide ? 0 : (axis === 'z' ? pw / 2 : 0));
        const handle = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.5, 0.08), mat('#56626a'));
        handle.position.set(slab.position.x + (axis === 'x' ? (d.slide ? (i ? -pw / 2 + 0.15 : pw / 2 - 0.15) : pw / 2 - 0.15) : 0.1), 1.1, slab.position.z + (axis === 'z' ? (d.slide ? (i ? -pw / 2 + 0.15 : pw / 2 - 0.15) : pw / 2 - 0.15) : 0.1));
        piv.add(slab, handle);
        piv.userData = { kind: 'door', id: d.id, slideDir: i === 0 ? -1 : 1, base: piv.position.clone() };
        piv.traverse(o => { o.userData.kind = 'door'; o.userData.id = d.id; });
        group.add(piv); panels.push(piv);
      }
      rt.panels = panels; rt.open = false; rt.solid = doorSolid(d, axis); solids.push(rt.solid);
    }
    const label = d.type === 'airshower' ? tr("氣浴室") : d.type === 'oneway' ? tr("單向門 ➜") : d.type === 'exit' ? tr("出口（單向）") : d.type === 'locked' ? tr("🔒 上鎖")
      : d.cold ? (d.name.includes('IQF') ? tr("IQF 冷凍室") : /凍|freez/i.test(d.name) ? tr("冷凍冰箱") : tr("冷藏冰箱")) : d.type === 'hidden' ? tr("拉門") : d.slide ? tr("碼頭拉門") : d.gate === 'wash' ? tr("🧼 洗手後進入") : d.gate === 'clock' ? tr("員工大門") : '';
    if (label) { const lb = makeLabel(label, undefined, { bg: 'rgba(16,26,32,0.7)', scale: 0.6 }); lb.position.set(d.pos[0], 2.8, d.pos[1]); group.add(lb); }
    if (d.type === 'airshower' || d.type === 'oneway' || d.type === 'exit') {
      const from = floor.zones.find(z => z.id === d.from)!, to = floor.zones.find(z => z.id === d.to)!;
      const fc = [from.rect[0] + from.rect[2] / 2, from.rect[1] + from.rect[3] / 2], tc = [to.rect[0] + to.rect[2] / 2, to.rect[1] + to.rect[3] / 2];
      const dir = axis === 'z' ? Math.sign(tc[0] - fc[0]) : Math.sign(tc[1] - fc[1]);
      const arrow = new THREE.Mesh(new THREE.ConeGeometry(0.3, 0.7, 3), new THREE.MeshBasicMaterial({ color: c, transparent: true, opacity: 0.9 }));
      arrow.rotation.x = -Math.PI / 2;
      if (axis === 'z') arrow.rotation.z = dir > 0 ? -Math.PI / 2 : Math.PI / 2; else arrow.rotation.z = dir > 0 ? Math.PI : 0;
      arrow.position.set(d.pos[0], 0.03, d.pos[1]); group.add(arrow);
    }
  }

  // 擺設
  for (const p of floor.decor) {
    let m: THREE.Mesh;
    if (p.cyl) {
      m = new THREE.Mesh(new THREE.CylinderGeometry(p.cyl[0], p.cyl[0] * 0.9, p.cyl[1], 18), mat(p.color));
      m.position.set(p.pos[0], p.y ?? p.cyl[1] / 2, p.pos[1]);
      if (p.solid !== false) solids.push(rect(p.pos[0], p.pos[1], p.cyl[0] * 2, p.cyl[0] * 2));
    } else {
      const s = p.size ?? [1, 1, 1];
      m = new THREE.Mesh(new THREE.BoxGeometry(s[0], s[1], s[2]), mat(p.color));
      m.position.set(p.pos[0], p.y ?? s[1] / 2, p.pos[1]);
      if (p.solid !== false && p.y == null) solids.push(rect(p.pos[0], p.pos[1], s[0], s[2]));
    }
    group.add(m);
  }

  // 設備
  const equipment = new Map<string, EquipRuntime>();
  const conveyors: ConveyorRt[] = [];
  for (const e of floor.equipment) {
    const rt = buildEquipment(e);
    // v1.1 暗門拉開時：貼滿公告的那片牆滑到旁邊（還在，可以按 E 拉回去恢復原狀）
    if (e.interaction?.type === 'slide' && opts.flags[e.interaction.flag]) { rt.group.position.x += e.size[0] + 0.1; rt.label.position.x += e.size[0] + 0.1; rt.box = rect(e.pos[0] + e.size[0] + 0.1, e.pos[1], e.size[0], e.size[2]); }
    group.add(rt.group); group.add(rt.label);
    const flat = e.kind === 'floorZone' || e.kind === 'switch' || e.interaction?.type === 'slide';
    if (e.solid !== false && !flat) solids.push(rt.box);
    equipment.set(e.id, rt);
    if (e.kind === 'conveyor' && (e.line || e.product)) {
      const axis: 'x' | 'z' = e.size[0] >= e.size[2] ? 'x' : 'z';
      const len = axis === 'x' ? e.size[0] : e.size[2];
      const spacing = e.line ? 1.1 : 0.8;
      const items: THREE.Mesh[] = [];
      for (let i = 0; i < Math.floor(len / spacing); i++) {
        const it = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.15, 0.32), uniqueMat('#ffffff'));
        it.userData.noShadow = true;   // v1.4.1 輸送帶上一直動的小東西不投影（省效能）
        it.position.set(e.pos[0], e.size[1] * 0.92 + 0.08, e.pos[1]); group.add(it); items.push(it);
      }
      conveyors.push({ eq: e, items, axis, len, spacing, line: e.line, product: e.product });
    }
  }

  const zoneAt = (x: number, z: number): Zone | null => {
    for (let i = floor.zones.length - 1; i >= 0; i--) {
      const [x0, z0, w, d] = floor.zones[i].rect;
      if (x >= x0 && x <= x0 + w && z >= z0 && z <= z0 + d) return floor.zones[i];
    }
    return null;
  };
  const anchor = (id: string) => {
    const e = equipment.get(id); if (e) return { x: e.data.pos[0], z: e.data.pos[1], box: e.box };
    const d = doorsRt.get(id); if (d) return { x: d.data.pos[0], z: d.data.pos[1], box: d.box };
    return null;
  };
  const doorRects = new Set([...doorsRt.values()].map(d => d.solid).filter(Boolean) as Rect[]);
  const walkable = (x: number, z: number, r = 0.35, ignoreManualDoors = false) => {
    if (x < -W / 2 + r || x > W / 2 - r || z < -D / 2 + r || z > D / 2 - r) return false;
    for (const b of solids) {
      if (ignoreManualDoors && doorRects.has(b)) continue;
      if (x > b.minX - r && x < b.maxX + r && z > b.minZ - r && z < b.maxZ + r) return false;
    }
    return true;
  };
  const setDoorOpen = (id: string, open: boolean) => {
    const d = doorsRt.get(id); if (!d?.solid || d.open === open) return;
    d.open = open;
    const i = solids.indexOf(d.solid);
    if (open && i >= 0) solids.splice(i, 1); else if (!open && i < 0) solids.push(d.solid);
  };
  return { floor, group, solids, equipment, doors: doorsRt, conveyors, zoneAt, anchor, walkable, setDoorOpen };
}

function buildEquipment(e: Equipment): EquipRuntime {
  const g = new THREE.Group();
  const [w, h, d] = e.size;
  const add = (geo: THREE.BufferGeometry, m: THREE.Material, x = 0, y = 0, z = 0) => { const o = new THREE.Mesh(geo, m); o.position.set(x, y, z); g.add(o); return o; };
  let screen: THREE.Mesh | undefined;
  const front = (dz = 0.03) => d / 2 + dz;
  switch (e.kind) {
    case 'conveyor':
      add(box(w, h * 0.85, d), mat(e.color), 0, h * 0.425);
      add(box(w * 0.98, 0.06, d * 0.98), isNewStyle() ? beltMat(w, d) : mat('#262b2e'), 0, h * 0.88);
      for (const s of [-1, 1]) add(box(w > d ? w : 0.06, 0.1, w > d ? 0.06 : d), mat('#8b959b'), w > d ? 0 : s * w / 2, h * 0.92, w > d ? s * d / 2 : 0);
      break;
    case 'drum': {
      add(box(w, h * 0.4, d), mat('#6b7a84'), 0, h * 0.2);
      const c = add(new THREE.CylinderGeometry(d * 0.42, d * 0.42, w * 0.95, 20), mat(e.color), 0, h * 0.62); c.rotation.z = Math.PI / 2; break;
    }
    case 'kettle':
      add(new THREE.CylinderGeometry(w * 0.48, w * 0.4, h, 22), mat(e.color), 0, h / 2);
      add(new THREE.TorusGeometry(w * 0.46, 0.05, 6, 22), mat('#8b959b'), 0, h).rotation.x = Math.PI / 2; break;
    case 'silo':
      add(new THREE.CylinderGeometry(w / 2, w / 2, h * 0.75, 24), mat(e.color), 0, h * 0.375 + h * 0.15);
      add(new THREE.ConeGeometry(w / 2, h * 0.2, 24), mat('#9aa4a9'), 0, h * 0.15 * 0.5 + 0.05).rotation.x = Math.PI;
      for (const [sx, sz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) add(new THREE.CylinderGeometry(0.06, 0.06, h * 0.25, 6), mat('#6b7a84'), sx * w * 0.35, h * 0.12, sz * d * 0.35);
      break;
    case 'floorZone': {
      const m = add(new THREE.PlaneGeometry(w, d), new THREE.MeshBasicMaterial({ color: e.color, transparent: true, opacity: 0.35, depthWrite: false }), 0, 0.015);
      m.rotation.x = -Math.PI / 2; break;
    }
    case 'shelf': {
      const frame = mat(e.color);
      for (const sx of [-1, 1]) for (const sz of [-1, 1]) add(box(0.06, h, 0.06), frame, sx * (w / 2 - 0.03), h / 2, sz * (d / 2 - 0.03));
      const levels = Math.max(2, Math.round(h / 0.6));
      const colors = e.goods?.colors ?? ['#c79c63'];
      const long = w >= d;
      for (let i = 0; i < levels; i++) {
        const y = 0.12 + i * (h - 0.2) / levels;
        add(box(w, 0.04, d), frame, 0, y);
        const n = Math.max(1, Math.floor((long ? w : d) / 0.55));
        for (let j = 0; j < n; j++) {
          if ((i * 7 + j * 3) % 5 === 0) continue; // 空位
          const bh = 0.22 + ((i + j) % 3) * 0.06;
          const off = -((long ? w : d) / 2) + 0.3 + j * ((long ? w : d) - 0.6) / Math.max(1, n - 1);
          add(box(long ? 0.42 : w * 0.75, bh, long ? d * 0.75 : 0.42), mat(colors[(i + j) % colors.length]), long ? off : 0, y + bh / 2 + 0.02, long ? 0 : off);
        }
      }
      break;
    }
    case 'desk':
      add(box(w, 0.06, d), mat(e.color), 0, h);
      for (const sx of [-1, 1]) add(box(0.06, h, d * 0.9), mat('#5b4a38'), sx * (w / 2 - 0.05), h / 2);
      screen = add(box(0.62, 0.4, 0.04), screenMat('#0c1114', '#3d8fb0'), 0, h + 0.3, -d / 2 + 0.2);
      add(box(0.4, 0.02, 0.15), mat('#2b3238'), 0, h + 0.04, 0.05);
      break;
    case 'locker':
      add(box(w, h, d), mat(e.color), 0, h / 2);
      for (let i = 1; i < Math.round(d / 0.5); i++) add(box(w + 0.01, h * 0.96, 0.02), mat('#5d6e7a'), 0, h / 2, -d / 2 + i * 0.5);
      break;
    case 'sink':
      add(box(w, h * 0.85, d), mat('#b8c4c9'), 0, h * 0.425);
      add(box(w * 0.8, 0.05, d * 0.7), mat('#6fa8c9'), 0, h * 0.86);
      add(new THREE.CylinderGeometry(0.03, 0.03, 0.3, 8), mat('#d9e2e6'), 0, h + 0.1, -d * 0.3);
      break;
    case 'bin':
      add(new THREE.CylinderGeometry(w / 2, w * 0.42, h, 16), mat(e.color), 0, h / 2);
      add(new THREE.CylinderGeometry(w / 2 + 0.03, w / 2 + 0.03, 0.06, 16), mat('#2f4a2f'), 0, h);
      break;
    case 'stairs':
      for (let i = 0; i < 6; i++) add(box(w, 0.12 + i * 0.18, d / 6), mat(e.color), 0, (0.12 + i * 0.18) / 2, d / 2 - (i + 0.5) * d / 6);
      add(box(0.05, 1.2, d), mat('#d6a23c'), w / 2, 0.9, 0);
      break;
    case 'freight': case 'lift':
      add(box(w, h, d), mat(e.color), 0, h / 2);
      add(box(w * 0.84, h * 0.82, 0.04), mat('#cfd8dc'), 0, h * 0.42, front());
      add(box(0.03, h * 0.82, 0.05), mat('#56626a'), 0, h * 0.42, front(0.05));
      add(box(w * 0.6, 0.22, 0.04), mat('#d6a23c', '#6b4a10'), 0, h * 0.9, front(0.05));
      break;
    case 'bed': {   // v0.6 醫務室單人床
      const long = d >= w;
      add(box(w, 0.35, d), mat('#9aa4a9'), 0, 0.2);
      add(box(w * 0.94, 0.16, d * 0.96), mat(e.color), 0, 0.45);
      add(box(long ? w * 0.7 : 0.35, 0.1, long ? 0.35 : d * 0.7), mat('#ffffff'), long ? 0 : -w * 0.35, 0.58, long ? -d * 0.35 : 0);
      add(box(long ? w * 0.95 : w * 0.55, 0.06, long ? d * 0.55 : d * 0.95), mat('#9fd3f5'), long ? 0 : w * 0.18, 0.55, long ? d * 0.18 : 0);
      break;
    }
    case 'switch':
      add(box(w, h, d), mat(e.color), 0, 1.3);
      break;
    case 'stove':
      add(box(w, h, d), mat('#5b656b'), 0, h / 2);
      add(box(w * 0.96, 0.04, d * 0.96), mat(e.color), 0, h + 0.02);
      for (const s of [-1, 1]) add(new THREE.TorusGeometry(0.18, 0.03, 6, 18), mat('#1d2226', '#7a2a10'), 0, h + 0.05, s * d * 0.25).rotation.x = Math.PI / 2;
      break;
    case 'microwave':
      add(box(w, h * 0.62, d), mat('#8e7a5f'), 0, h * 0.31);
      add(box(w * 0.9, h * 0.36, d * 0.9), mat(e.color), 0, h * 0.8);
      screen = add(box(w * 0.55, h * 0.24, 0.03), screenMat('#1d2226', '#2a4a5a'), -w * 0.1, h * 0.8, front());
      break;
    case 'pot':
      add(box(w, h, d), mat('#c7ced2'), 0, h / 2);
      for (let i = 0; i < 3; i++) add(new THREE.CylinderGeometry(0.22 - i * 0.03, 0.2 - i * 0.03, 0.25, 16), mat(e.color), -w / 3 + i * w / 3, h + 0.13, 0);
      break;
    case 'labbench':
      add(box(w, 0.08, d), mat(e.color), 0, h);
      add(box(w * 0.96, h - 0.1, d * 0.9), mat('#c7ced2'), 0, (h - 0.1) / 2);
      for (let i = 0; i < 4; i++) add(new THREE.CylinderGeometry(0.05, 0.05, 0.22, 10), litMat({ color: ['#9fd3f5', '#f2b134', '#e06c75', '#7fbf98'][i], transparent: true, opacity: 0.8 }), -w / 3 + i * 0.3, h + 0.15, -d * 0.2);
      add(box(0.5, 0.35, 0.4), mat('#e8eef0'), w * 0.3, h + 0.2, 0);
      break;
    case 'board':
      if (isNewStyle()) { add(box(w, h, d), boardMat(), 0, 1.0 + h / 2); break; }
      add(box(w, h, d), mat(e.color), 0, 1.0 + h / 2);
      for (let i = 0; i < 4; i++) add(box(w > d ? w * 0.18 : 0.02, 0.22, w > d ? 0.02 : d * 0.18), mat(['#f4f6f6', '#f2d33c', '#9fd3f5', '#f4a7c0'][i]),
        w > d ? -w * 0.3 + i * w * 0.2 : (Math.sign(e.pos[0]) >= 0 ? -1 : 1) * 0.04, 1.0 + h * 0.55 + (i % 2) * 0.12, w > d ? (Math.sign(e.pos[1]) >= 0 ? -1 : 1) * 0.04 : -d * 0.3 + i * d * 0.2);
      break;
    case 'dryer': case 'sprayer':
      add(box(w, h, d), mat(e.color), 0, (e.kind === 'dryer' ? 1.2 : 0.9) + h / 2);
      add(box(w * 0.6, 0.06, d * 0.6), mat('#56626a'), 0, (e.kind === 'dryer' ? 1.2 : 0.9) - 0.02);
      break;
    case 'scale':
      add(box(w, h, d), mat(e.color), 0, h / 2);
      add(box(0.5, 0.06, 0.4), mat('#e8eef0'), -w * 0.2, h + 0.03, 0);
      screen = add(box(0.3, 0.14, 0.04), screenMat('#0c1114', '#4fd58a'), w * 0.25, h + 0.1, d * 0.2);
      break;
    case 'truck': {
      const along = w >= d ? 'x' : 'z', L = Math.max(w, d), Wd = Math.min(w, d);
      const tbox = add(along === 'x' ? box(L * 0.72, h, Wd) : box(Wd, h, L * 0.72), mat(e.color), along === 'x' ? -L * 0.12 * Math.sign(e.pos[0] || 1) * -1 : 0, h / 2 + 0.3, along === 'z' ? L * 0.12 : 0);
      void tbox;
      add(along === 'x' ? box(L * 0.26, h * 0.7, Wd * 0.95) : box(Wd * 0.95, h * 0.7, L * 0.26), mat('#3f78a8'), along === 'x' ? -L * 0.37 * Math.sign(e.pos[0] || 1) * -1 : 0, h * 0.35 + 0.3, along === 'z' ? -L * 0.37 : 0);
      for (const a of [-0.3, 0.3]) for (const b of [-1, 1]) { const wh = add(new THREE.CylinderGeometry(0.32, 0.32, 0.25, 14), mat('#1d2226'), along === 'x' ? a * L : b * Wd / 2, 0.32, along === 'z' ? a * L : b * Wd / 2); wh.rotation[along === 'x' ? 'x' : 'z'] = Math.PI / 2; }
      break;
    }
    case 'mirror':
      add(box(w, h, d), litMat({ color: e.color, emissive: '#2a3a40' }), 0, 0.6 + h / 2);
      break;
    case 'bigdesk':
      add(box(w, 0.08, d), mat(e.color), 0, h);
      add(box(w * 0.96, h - 0.05, d * 0.3), mat('#3f2c1c'), 0, (h - 0.05) / 2, d * 0.3);
      for (const sx of [-1, 1]) add(box(0.5, h, d * 0.9), mat('#4a3424'), sx * (w / 2 - 0.25), h / 2);
      screen = add(box(0.7, 0.42, 0.04), screenMat('#0c1114', '#3d8fb0'), -w * 0.2, h + 0.3, -d / 2 + 0.25);
      add(box(0.5, 0.12, 0.3), mat('#c9473f'), w * 0.25, h + 0.1, 0);   // 一盒喉糖、一疊報廢單
      break;
    case 'cart':
      add(box(w, h * 0.7, d), mat(e.color), 0, h * 0.35 + 0.15);
      for (const s of [-1, 1]) add(box(0.06, 0.4, d * 0.8), mat('#5b5b5b'), s * (w / 2 + 0.03), h * 0.8);
      break;
    default: add(box(w, h, d), mat(e.color), 0, h / 2);
  }
  if (e.kind === 'terminal' || e.kind === 'panel' || e.kind === 'oven' || e.kind === 'machine') {
    const sw = Math.min(w * 0.6, 1.2);
    screen = add(box(sw, Math.min(0.42, h * 0.3), 0.05), screenMat('#0c1114', '#3d8fb0'), 0, h * 0.68, front());
    // v1.4 新版：機台、烤箱正面加一塊控制面板（按鈕、指示燈、緊急停止）
    if (isNewStyle() && (e.kind === 'machine' || e.kind === 'oven') && w >= 0.7 && h >= 0.9) {
      const ps = Math.min(0.42, w * 0.28, h * 0.3);
      add(new THREE.PlaneGeometry(ps, ps), panelMat(), w / 2 - ps / 2 - 0.06, h * 0.4, front(0.035));
    }
  }
  if (e.kind === 'freezer') {
    add(box(Math.min(1.1, w * 0.4), Math.min(2.0, h * 0.85), 0.06), mat('#b8c4c9'), 0, Math.min(2.0, h * 0.85) / 2, front());
    screen = add(box(0.3, 0.16, 0.05), screenMat('#0c1114', '#4fd58a'), w * 0.3, h * 0.75, front(0.04));
  }
  if (isNewStyle()) mergeByMaterial(g, screen);   // v1.4.1 同一台機器、同一種材質的零件合成一個（繪製次數少很多）
  g.position.set(e.pos[0], 0, e.pos[1]);
  // v1.1 正面朝向（電梯門不能對著牆）
  if (e.facing && e.facing !== 's') g.rotation.y = { n: Math.PI, e: Math.PI / 2, w: -Math.PI / 2 }[e.facing];
  const side = e.facing === 'e' || e.facing === 'w';
  g.traverse(o => { o.userData.kind = 'equipment'; o.userData.id = e.id; });
  const label = makeLabel(e.name.split(tr("・"))[0].split(tr("（"))[0].trim(), undefined, { scale: 0.72 });
  label.position.set(e.pos[0], Math.max(h, 1.2) + 0.55, e.pos[1]);
  return { data: e, group: g, box: side ? rect(e.pos[0], e.pos[1], d, w) : rect(e.pos[0], e.pos[1], w, d), label, screen };
}

/** 手動門的開關動畫：推門轉 90 度、拉門往兩邊滑開 */
export function animateDoors(rt: FloorRuntime, dt: number): void {
  for (const d of rt.doors.values()) {
    if (!d.panels) continue;
    for (const p of d.panels) {
      const ud = p.userData as { t?: number; slideDir: number; base: THREE.Vector3 };
      const target = d.open ? 1 : 0; const t0 = ud.t ?? 0;
      const t = t0 + Math.sign(target - t0) * Math.min(Math.abs(target - t0), dt * 3.2);
      ud.t = t;
      if (d.data.slide) {
        const pw = d.data.width / 2;
        p.position.copy(ud.base);
        if (d.axis === 'x') p.position.x += ud.slideDir * t * pw * 0.95; else p.position.z += ud.slideDir * t * pw * 0.95;
      } else p.rotation.y = -t * Math.PI / 2 * (d.axis === 'x' ? 1 : -1);
    }
  }
}

// ------------------------------------------------------------------ v0.7 地板材質
export type FloorKind = 'epoxy-cyan' | 'epoxy-green' | 'tile-graygreen' | 'epoxy-gray' | 'plain-white' | 'outdoor';
export function floorKind(z: Floor['zones'][number]): FloorKind {
  if (z.floor) return z.floor;
  if (z.outdoor) return 'outdoor';
  if (z.cold) return 'epoxy-gray';
  return z.level === 2 ? 'epoxy-cyan' : z.level === 1 ? 'epoxy-green' : z.level === 0 ? 'epoxy-gray' : 'plain-white';
}
export const FLOOR_COLOR: Record<FloorKind, string> = {
  'epoxy-cyan': '#4fa7b8', 'epoxy-green': '#5f9e6e', 'tile-graygreen': '#7f9689', 'epoxy-gray': '#9ca6ab', 'plain-white': '#e3e1db', outdoor: '#55625a',
};
const tileTex = new Map<string, THREE.CanvasTexture>();
function tileTexture(base: string, grout: string): THREE.CanvasTexture {
  const key = base + grout; const hit = tileTex.get(key); if (hit) return hit;
  const cv = document.createElement('canvas'); cv.width = cv.height = 64; const x = cv.getContext('2d')!;
  x.fillStyle = grout; x.fillRect(0, 0, 64, 64); x.fillStyle = base; x.fillRect(2, 2, 60, 60);
  x.fillStyle = 'rgba(255,255,255,0.06)'; for (let i = 0; i < 40; i++) x.fillRect(4 + (i * 37) % 56, 4 + (i * 23) % 56, 2, 2);   // 防滑顆粒
  const t = new THREE.CanvasTexture(cv); t.wrapS = t.wrapT = THREE.RepeatWrapping; t.colorSpace = THREE.SRGBColorSpace; tileTex.set(key, t); return t;
}
function floorMaterial(k: FloorKind, w: number, d: number): THREE.Material {
  const nc = floorColorNew(k);   // v1.4 新版：地板依清潔等級各一色（色票）
  if (k === 'tile-graygreen') {
    if (typeof document === 'undefined') return litMat({ color: nc ?? FLOOR_COLOR[k] });
    const t = tileTexture(nc ?? FLOOR_COLOR[k], nc ? '#7a8f81' : '#62766a').clone(); t.needsUpdate = true; t.repeat.set(w / 0.6, d / 0.6);
    if (nc) return new THREE.MeshStandardMaterial({ map: t, roughness: 0.78, metalness: 0 });
    return litMat({ map: t });
  }
  if (k === 'plain-white' || k === 'outdoor') return nc ? new THREE.MeshStandardMaterial({ color: nc, roughness: 0.92, metalness: 0 }) : litMat({ color: FLOOR_COLOR[k] });
  return new THREE.MeshStandardMaterial({ color: nc ?? FLOOR_COLOR[k], roughness: nc ? 0.28 : 0.32, metalness: 0.08 });   // epoxy：亮亮的
}

// ------------------------------------------------------------------ v1.4.1 合併零件：同一個群組裡、同一種材質的方塊／圓柱合成一個網格
function mergeByMaterial(g: THREE.Group, keep?: THREE.Object3D): void {
  const buckets = new Map<THREE.Material, THREE.Mesh[]>();
  for (const c of [...g.children]) {
    const m = c as THREE.Mesh;
    if (!m.isMesh || m === keep || Array.isArray(m.material)) continue;
    const k = m.material as THREE.Material; const list = buckets.get(k); if (list) list.push(m); else buckets.set(k, [m]);
  }
  for (const [material, list] of buckets) {
    if (list.length < 2) continue;
    const geos = list.map(m => { m.updateMatrix(); const gg = (m.geometry.index ? m.geometry.toNonIndexed() : m.geometry.clone()); gg.applyMatrix4(m.matrix); return gg; });
    let merged: THREE.BufferGeometry | null = null;
    try { merged = mergeGeometries(geos, false); } catch { merged = null; }
    for (const x of geos) x.dispose();
    if (!merged) continue;
    for (const m of list) { g.remove(m); if (!m.geometry.userData.shared) m.geometry.dispose(); }
    g.add(new THREE.Mesh(merged, material));
  }
}

// ------------------------------------------------------------------ v1.4 新版細節材質（canvas 貼圖）
/** 螢幕：經典版＝原本的單色發光；新版＝畫了介面的發光螢幕（後製會微微發亮） */
function screenMat(base: string, glow: string): THREE.MeshLambertMaterial {
  if (!isNewStyle()) return uniqueMat(base, glow);
  const tint = glow === '#4fd58a' ? '#6fe0a0' : glow === '#2a4a5a' ? '#7fb4c8' : '#5ec8e6';
  const tex = screenTexture(tint);
  const m = new THREE.MeshStandardMaterial({ color: '#ffffff', map: tex, emissive: '#ffffff', emissiveMap: tex, emissiveIntensity: 2.6, roughness: 0.35, metalness: 0 });
  return m as unknown as THREE.MeshLambertMaterial;
}
let panelM: THREE.Material | null = null, boardM: THREE.Material | null = null;
function panelMat(): THREE.Material { return panelM ??= new THREE.MeshStandardMaterial({ map: panelTexture(), roughness: 0.5, metalness: 0.1 }); }
function boardMat(): THREE.Material { return boardM ??= new THREE.MeshStandardMaterial({ map: boardTexture(), roughness: 0.9, metalness: 0 }); }
const beltCache = new Map<string, THREE.Material>();
function beltMat(w: number, d: number): THREE.Material {
  const long = Math.max(w, d), key = `${Math.round(long * 2)}|${w >= d}`;
  const hit = beltCache.get(key); if (hit) return hit;
  const base = beltTexture(); const t = base?.clone() ?? null;
  if (t) { t.userData.shared = true; t.needsUpdate = true; if (w >= d) t.repeat.set(long * 4, 1); else { t.repeat.set(1, long * 4); t.rotation = Math.PI / 2; } }
  const m = new THREE.MeshStandardMaterial({ map: t, roughness: 0.7, metalness: 0 });
  beltCache.set(key, m); return m;
}
